import './console.override'; // ✅ MUST BE FIRST
import { environment } from "./environments/environment";
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, enableProdMode, importProvidersFrom, isDevMode, inject } from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { DEFAULTS, SETTINGS } from "@angular/fire/compat/remote-config";
import { getFirestore, provideFirestore } from "@angular/fire/firestore";
import { getRemoteConfig, provideRemoteConfig } from "@angular/fire/remote-config";
import { getStorage, provideStorage } from "@angular/fire/storage";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { RouteReuseStrategy } from "@angular/router";
import { FirebaseModule } from "@core/firebase.module";
import { IndexedDBErrorHandler } from "@core/indexed-db-error.handler";
import { IonicModule, IonicRouteStrategy } from "@ionic/angular";
import { IonicStorageModule } from "@ionic/storage-angular";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import { provideLottieOptions } from "ngx-lottie";
import { SwiperModule } from "swiper/angular";
import { AppCheckInterceptor } from "./app/app-check-interceptor";
import { AppRoutingModule } from "./app/app-routing.module";
import { AppComponent } from "./app/app.component";
import { appInitializerFactory, initializeAppCustomLogic } from "./app/app.module";
import { ComponentModule, FirestoreTranslationsLoaderFactory } from "./app/components/component.module";
import { CustomRouteReuseStrategy } from "./app/customeRouteStatergy/custom-route-reuse-strategy";
import { DirectiveModule } from "./app/directives/directive.module";
import { CashoutModule } from "./app/pages/cashout/cashout.module";
import { CheckWinningsModule } from "./app/pages/check-winnings/check-winnings.module";
import { GamesPageModule } from "./app/pages/games/games.module";
import { SalesModule } from "./app/pages/sales/sales.module";
import { StorePageModule } from "./app/pages/store/store.module";
import { SyndicatePageModule } from "./app/pages/syndicate/syndicate.module";
import { AuthService } from "@core/auth.service";
import { initSentry, setSentryErrorLogger } from "./app/Sentry/sentry.config";
import { GlobalErrorHandler, RollbarService, rollbarFactory } from "./app/services/global-error-handler";
import { InitFactoryService } from "./app/services/init-factory.service";
import { registerAppServiceWorker } from "./app/ServiceWorker/service-worker.config";
import { playerFactory } from './app/shared/lottie-config';
declare global {
  interface Window {
    __lsAngularBootstrapped?: boolean;
    Android?: {
      showHTML?: (html: string) => void;
      setContactAutofillValuesFromBridge?: (values: any) => void;
      [key: string]: any; // Allows future Android bridge methods safely
    };
  }
}

// Sanitize autofill data utility function
function sanitizeAutofillData(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  try {
    if (Array.isArray(data)) {
      return data
        .filter((element) => element !== null && typeof element !== 'undefined')
        .map((element) => {
          if (typeof element === 'object') {
            return sanitizeAutofillData(element);
          }
          return element;
        });
    }

    if (typeof data === 'object' && !Array.isArray(data)) {
      const sanitized: any = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          const value = data[key];
          if (value !== null && typeof value !== 'undefined') {
            sanitized[key] = sanitizeAutofillData(value);
          }
        }
      }
      return sanitized;
    }

    return data;
  } catch (error) {
    if (!environment.production) {
      console.warn('Data sanitization failed:', error);
    }
    return data;
  }
}

// Initialize bridge safely
if (typeof window !== 'undefined') {
  // Create base Android object if missing
  if (!window.Android) {
    window.Android = {};
  }

  // Polyfill showHTML if missing
  if (!window.Android.showHTML) {
    window.Android.showHTML = function (html: string): void {
      if (!environment.production) {
        console.warn(
          'window.Android.showHTML called in non-WebView context. HTML:',
          html
        );
      }
      // Silent no-op in production
    };
  }

  // Polyfill setContactAutofillValuesFromBridge if missing
  // Enhanced with data sanitization to prevent undefined array element errors
  if (!window.Android.setContactAutofillValuesFromBridge) {
    window.Android.setContactAutofillValuesFromBridge = function (
      values: any
    ): void {
      try {
        // Sanitize incoming data to remove undefined/null elements
        const sanitizedValues = sanitizeAutofillData(values);

        if (!environment.production) {
          if (sanitizedValues !== values) {
            console.warn(
              'Autofill data was sanitized - removed undefined elements from arrays'
            );
          }
          console.warn(
            'window.Android.setContactAutofillValuesFromBridge called with sanitized values:',
            sanitizedValues
          );
        }
        // Silent no-op in production
      } catch (error) {
        if (!environment.production) {
          console.error('Error in setContactAutofillValuesFromBridge:', error);
        }
      }
    };
  }
}

export { };
if (environment.production) {
  enableProdMode();
  window.console.log = function () { };
}

// Initialize Sentry
initSentry();

/**
 * Normalize query-param KEY casing BEFORE Angular's initial navigation, but only
 * for the partial-registration / interim flows.
 *
 * Affiliate links land on these routes with uppercase keys (e.g. OL, Other, TP1).
 * The app lowercases them via AuthPartialRegService.normalizeQueryParams(), but that
 * runs AFTER the route has resolved and does a router navigation, which re-triggers
 * the route resolver (a second blocking partialregv2 call). During that second
 * resolve the outlet is empty, producing a long white blank screen on slow networks.
 *
 * Cleaning the URL up-front (via history.replaceState, no router involvement) means
 * the resolver runs exactly once and the in-app normalize becomes a no-op. Scoped to
 * these paths so other routes that may rely on original key casing are unaffected.
 */
function normalizePartialRegUrlEarly(): void {
  try {
    const path = (window.location.pathname || "").toLowerCase();
    if (!path.includes("partial-registration") && !path.includes("interim")) {
      return;
    }
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search || "");
    let changed = false;
    const lowerCased = new Map<string, string>();
    for (const [key, value] of params.entries()) {
      const lowerKey = key.toLowerCase();
      if (!lowerCased.has(lowerKey)) {
        lowerCased.set(lowerKey, value);
      } else {
        changed = true;
      }
      if (key !== lowerKey) changed = true;
    }
    if (changed) {
      const newParams = new URLSearchParams(Array.from(lowerCased.entries()));
      const cleanUrl = `${url.pathname}?${newParams.toString()}${url.hash || ""}`;
      window.history.replaceState(window.history.state, "", cleanUrl);
    }
  } catch {
    // Non-fatal: the existing in-app normalization remains as a fallback.
  }
}
normalizePartialRegUrlEarly();

bootstrapApplication(AppComponent, {
  providers: [
    provideLottieOptions({ player: playerFactory }),
    importProvidersFrom(
      BrowserModule,
      IonicModule.forRoot({ mode: "ios" }),
      IonicStorageModule.forRoot({
        name: "__mydb",
        driverOrder: ["sqlite", "websql", "indexeddb"],
        size: 100 * 1024 * 1024,
      }),
      CommonModule,
      AppRoutingModule,
      DirectiveModule,
      SwiperModule,
      FormsModule,
      ReactiveFormsModule,
      StorePageModule,
      SyndicatePageModule,
      CheckWinningsModule,
      ComponentModule,
      SalesModule,
      FirebaseModule,
      CashoutModule,
      GamesPageModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: FirestoreTranslationsLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideStorage(() => getStorage()),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
    { provide: Window, useValue: window },
    { provide: DEFAULTS, useValue: { enableAwesome: true } },
    {
      provide: SETTINGS,
      useFactory: () => isDevMode()
        ? { minimumFetchIntervalMillis: 10000, fetchTimeoutMillis: 15000 }
        : {},
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppCheckInterceptor,
      multi: true,
    },
    { provide: APP_INITIALIZER, useFactory: initializeAppCustomLogic, deps: [InitFactoryService], multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        return () => setSentryErrorLogger(authService.errorHandlerLogs.bind(authService));
      },
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initCleverTap,
    //   deps: [ClevertapService],
    //   multi: true,
    // },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: ErrorHandler, useClass: IndexedDBErrorHandler },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy }
  ]
})
.then(() => {
  window.__lsAngularBootstrapped = true;
  window.dispatchEvent(new Event("ls:angular-bootstrapped"));
  return Promise.resolve().then(() => registerAppServiceWorker()).catch((error) => {
    Sentry.captureException(error, {
      tags: { phase: "service-worker-registration" },
    });
  });
})
.catch((error) => {
  Sentry.captureException(error, {
    tags: { phase: "angular-bootstrap" },
  });
  window.dispatchEvent(new Event("ls:angular-bootstrap-failed"));
});

